import {
  firestore,
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteField,
  query,
  where,
  writeBatch,
} from "@/firebase/init";
import colors from "vuetify/lib/util/colors";
import { defaultUserRoles, buildRoleDoc } from "./phase0_defaultUserSettings";

/**
 * Resolve Vuetify theme color name (e.g. "indigo", "blue lighten-3") to hex for inline styles.
 */
function resolveBadgeColorToHex(colorName) {
  if (!colorName || typeof colorName !== "string") return null;
  const trimmed = colorName.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/);
  const familyKebab = parts[0];
  const modifier = parts[1]
    ? parts[1].replace(/-/, "").toLowerCase()
    : "base";
  const family =
    familyKebab === "black" || familyKebab === "white" || familyKebab === "transparent"
      ? "shades"
      : familyKebab
          .split("-")
          .map((s, i) => (i === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1)))
          .join("");
  const familyObj = colors[family];
  if (!familyObj) return null;
  const hex =
    family === "shades"
      ? familyObj[familyKebab]
      : familyObj[modifier] ?? familyObj.base;
  return hex || null;
}

function _defaultVP(accountType) {
  if (accountType >= 4) return { create: true, edit: true, exportFile: true };
  if (accountType >= 3) return { create: true, edit: true, exportFile: true };
  if (accountType >= 2) return { create: true, edit: false, exportFile: false };
  return { create: false, edit: false, exportFile: false };
}

function _accountTypeLabel(accountType) {
  if (accountType >= 4) return "internal";
  if (accountType >= 3) return "manager";
  if (accountType >= 2) return "user";
  return "vendor";
}

function _buildAdminRoleDoc(baseDoc, allViewValues) {
  const fullVP = allViewValues.reduce((acc, v) => {
    acc[v] = { create: true, edit: true, exportFile: true };
    return acc;
  }, {});
  const block = {
    views: [...allViewValues],
    viewPermissions: fullVP,
    dashboards: [],
    reports: [],
    permissions: [],
    leaderboard: false,
    filterLocation: false,
    department: "administration",
  };
  const makeBlock = () => ({
    config: { ...block, viewPermissions: { ...fullVP } },
    custom: JSON.parse(JSON.stringify(block)),
  });
  return {
    ...baseDoc,
    internal: makeBlock(),
  };
}

async function _migrateUserViewPermissions(roleDocs, allViewValues = []) {
  const flagRef = doc(firestore, "config", "viewPermissionsMigration");
  const flagSnap = await getDoc(flagRef);
  const flagData = flagSnap.exists() ? flagSnap.data() : {};
  const alreadyMigrated = flagData.migrated === true;

  const needsAdminProcessing =
    allViewValues.length > 0 &&
    (flagData.adminViewCount || 0) !== allViewValues.length;
  const needsNonAdminProcessing = !alreadyMigrated;

  // Nothing to do — skip expensive user collection read
  if (!needsAdminProcessing && !needsNonAdminProcessing) return;

  const usersSnap = await getDocs(collection(firestore, "users"));
  if (usersSnap.empty) return;

  const roleMap = {};
  for (const rd of roleDocs) {
    roleMap[rd.id] = rd.data();
  }

  const BATCH_LIMIT = 400;
  let batch = writeBatch(firestore);
  let opCount = 0;

  for (const userDoc of usersSnap.docs) {
    const data = userDoc.data();
    const accountType = data.accountType ?? 2;
    const isAdmin = accountType >= 4;
    const updateData = {};

    if (isAdmin && needsAdminProcessing) {
      // Set role to "admin" and give full viewPermissions for all views
      if (data.role !== "admin") updateData.role = "admin";
      const existingVP = data.viewPermissions || {};
      for (const v of allViewValues) {
        const vp = existingVP[v];
        if (!vp || vp.create !== true || vp.edit !== true || vp.exportFile !== true) {
          updateData[`viewPermissions.${v}`] = { create: true, edit: true, exportFile: true };
        }
      }
    } else if (!isAdmin && needsNonAdminProcessing) {
      const views = Array.isArray(data.views) ? data.views : [];
      if (views.length === 0) continue;
      const existingVP = data.viewPermissions || {};
      const missingViews = views.filter((v) => !existingVP[v]);
      if (missingViews.length === 0) continue;
      const roleValue = data.role || null;
      const label = _accountTypeLabel(accountType);
      const roleCustomVP =
        roleMap[roleValue]?.[label]?.custom?.viewPermissions || {};
      const fallback = _defaultVP(accountType);
      for (const v of missingViews) {
        updateData[`viewPermissions.${v}`] = roleCustomVP[v] || fallback;
      }
    }

    if (!alreadyMigrated && data.department === "admin") {
      updateData.department = "administration";
    }

    if (Object.keys(updateData).length === 0) continue;
    batch.update(doc(firestore, "users", userDoc.id), updateData);
    opCount++;

    if (opCount >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(firestore);
      opCount = 0;
    }
  }

  if (opCount > 0) await batch.commit();

  const flagUpdate = {};
  if (!alreadyMigrated) flagUpdate.migrated = true;
  if (needsAdminProcessing) flagUpdate.adminViewCount = allViewValues.length;
  if (Object.keys(flagUpdate).length > 0) {
    await setDoc(flagRef, flagUpdate, { merge: true });
  }
}

const state = {
  user: null,
  oAuthToken: null,
  accountTypes: [
    {
      text: "Internal",
      value: 4,
      desc: "Full visibility, like a manager but does not need to have views set.",
      color: "black",
      badge: {
        light: {
          color: "indigo",
          textColor: "white",

          icon: "mdi-shield-key",
        },
        dark: {
          color: "deep-purple lighten-1",
          textColor: "white",

          icon: "mdi-shield-key",
        },
      },
    },
    {
      text: "manager",
      value: 3,
      desc: "Has more permissions than a standard user, can approve orders/submit payments/etc.",
      color: "green",
      badge: {
        light: {
          color: "blue",
          textColor: "white",

          icon: "mdi-account-tie",
        },
        dark: {
          color: "blue darken-4",
          textColor: "white",

          icon: "mdi-account-tie",
        },
      },
    },
    {
      text: "user",
      value: 2,
      desc: "Standard user, will need to request manager to approve orders/etc.",
      color: "blue",
      badge: {
        light: {
          color: "blue lighten-3",
          textColor: "black",

          icon: "mdi-account",
        },
        dark: {
          color: "light-blue",
          textColor: "white",

          icon: "mdi-account",
        },
      },
    },
    {
      text: "vendor",
      value: 1,
      desc: "3rd party given access to your MyTrace instance. Account search bar & most features will be hidden/disabled.",
      color: "grey",
      badge: {
        light: {
          color: "grey lighten-2",
          textColor: "grey darken-2",

          icon: "mdi-handshake",
        },
        dark: {
          color: "grey darken-3",
          textColor: "grey lighten-2",

          icon: "mdi-handshake",
        },
      },
    },
  ],
  mytracePermissions: [
    {
      text: "Brand - Show all active",
      desc: "Show all active brands to this user",
      value: "allBrands",
      phase: "admin",
    },
    {
      text: "Brand - Show all related countries",
      desc: "Allow this user to see all countries of the selected brand (user's only see their assigned country by default)",
      value: "allCountries",
      phase: "admin",
    },
    {
      text: "Bypass Order - Manually add materials to location",
      desc: "Allows the user to manually add raw materials to a location. Normally only available for admin users.",
      value: "rawMaterials",
      phase: "admin",
    },
    {
      text: "Export data",
      desc: "By default, only manager+ can export data from mytrace.",
      value: "exportData",
      phase: "admin",
    },
    {
      text: "Import external orders",
      desc: "Allows the user to import orders from external systems",
      value: "importExternalOrders",
      phase: "admin",
    },
    {
      text: "Cannot apply discounts to sales orders",
      desc: "The 'discount' field will be disabled for this user",
      value: "cannotApplyDiscounts",
      phase: "sales",
    },
    {
      text: "Set special pricing on a account",
      desc: "Only manager+ users can set special pricing (Product is regularly $14, account gets special deal for $12)",
      value: "specialAccountPricing",
      phase: "sales",
    },
    {
      text: "Edit account status",
      desc: "Only manager+ users can edit account status",
      value: "editAccountStatus",
      phase: "sales",
    },
    {
      text: "Edit sales order line items",
      desc: "Only manager+ users can edit order line items once the status is >= accepted",
      value: "lineItems",
      phase: "sales",
    },
    {
      text: "Add payments to sales orders",
      desc: "Only manager+ or users with the 'accounting' role can add payments",
      value: "payments",
      phase: "sales",
    },

    {
      text: "Apply credits to sales orders",
      desc: "Allows the user to apply promotion credits to new sales orders during order creation",
      value: "applyCredits",
      phase: "sales",
    },
  ],
  departments: [
    { text: "Administration", value: "administration", sort: 1 },
    { text: "Support", value: "support", sort: 2 },
    { text: "IT/Tech", value: "tech", sort: 3 },
    { text: "Accounting/Finances", value: "accounting", sort: 4 },
    { text: "Legal/Compliance", value: "legal", sort: 5 },
    { text: "Manufacturing", value: "manufacturing", sort: 6 },
    { text: "Logistics/Delivery", value: "delivery", sort: 7 },
    { text: "Sales", value: "sales", sort: 8 },
    { text: "Graphic Design/Creative", value: "creative", sort: 9 },
    { text: "Marketing", value: "marketing", sort: 10 },
  ],
  userRoles: [
    { text: "Account Executive", value: "accountExecutive" },
    { text: "Account Manager", value: "accountManager" },
    { text: "Brand Ambassador", value: "brandAmbassador" },
    { text: "Event Manager", value: "eventManager" },
    { text: "Ticket Rep", value: "ticketRep" },
    { text: "Ticket Manager", value: "ticketManager" },
    { text: "Inventory Manager", value: "inventoryManager" },
    { text: "Accounting", value: "accounting" },
    { text: "Call Center Representative", value: "callCenterRepresentative" },
    { text: "Driver", value: "driver" },
    { text: "Admin", value: "admin" },
  ],
  userPresets: [
    {
      text: "Inventory/Warehouse",
      value: "inventoryManager",
      accountType: 2,
      desc: "Manages location inventory & order fulfillment",
      department: "manufacturing",
      role: null,
      leaderboard: false,
      permissions: ["lineItems"],
      dashboards: ["fulfillment"],
      views: [
        "tasks",
        "locations",
        "testingLabs",
        "rawMaterialVendors",
        "rawMaterialOrders",
        "draftOrders",
        "salesOrders",
        "deliveredOrders",
        "reports-overview",
      ],
    },
    {
      text: "Manufacturing/Work Orders",
      value: "workOrders",
      accountType: 2,
      desc: "Manages work orders & manufacturing products",
      department: "manufacturing",
      permissions: [],
      role: null,
      leaderboard: false,
      dashboards: ["manufacturing"],
      views: ["tasks", "locations", "rawMaterialOrders"],
    },
    {
      text: "Account Manager",
      value: "salesRep",
      desc: "Manages Account Executives, Sales",
      accountType: 3,
      department: "sales",
      leaderboard: true,
      role: "accountManager",
      permissions: [],
      dashboards: ["sales manager"],
      views: [
        "tasks",
        "parentAccounts",
        "accounts",
        "account-onboarding",
        "draftOrders",
        "salesOrders",
        "deliveredOrders",
        "events-overview",
        "budtender-training",
        "reports-overview",
      ],
    },
    {
      text: "Account Executive",
      value: "accountExecutive",
      accountType: 2,
      desc: "Manages Accounts, creates orders & supports growth with trainings/store events",
      department: "sales",
      role: "accountExecutive",
      leaderboard: true,
      permissions: [],
      dashboards: ["sales rep"],
      views: [
        "tasks",
        "parentAccounts",
        "accounts",
        "account-onboarding",
        "draftOrders",
        "salesOrders",
        "deliveredOrders",
        "events-overview",
        "budtender-training",
        "reports-overview",
      ],
    },
    {
      text: "Accounting",
      value: "accounting",
      desc: "Apply payments/credits, Create AR reports & manage financies",
      department: "accounting",
      leaderboard: false,
      role: "accounting",
      accountType: null,
      permissions: [],
      dashboards: [],
      views: [
        "tasks",
        "vendorOrders",
        "customProducts",
        "customOrders",
        "rawMaterialOrders",
        "parentAccounts",
        "accounts",
        "salesOrders",
        "deliveredOrders",
        "promotions-overview",
        "reports-overview",
      ],
    },
    {
      text: "Call Center Representative",
      value: "callCenterRepresentative",
      desc: "Handle incoming calls from customers & support sales orders",
      department: "support",
      accountType: 2,
      leaderboard: true,
      role: "callCenterRepresentative",
      permissions: [],
      dashboards: [],
      views: [
        "tasks",
        "parentAccounts",
        "accounts",
        "draftOrders",
        "salesOrders",
        "deliveredOrders",
        "events-overview",
        "supportTickets-overview",
        "reports-overview",
      ],
    },
    {
      text: "Support/Returns",
      value: "support",
      desc: "Manage Support Tickets, handle returns/defects from Accounts & misc support",
      department: "support",
      accountType: 2,
      leaderboard: false,
      role: "ticketManager",
      permissions: [],
      dashboards: [],
      views: [
        "tasks",
        "accounts",
        "salesOrders",
        "events-overview",
        "supportTickets-overview",
        "reports-overview",
      ],
    },
    {
      text: "Event Manager",
      value: "eventManager",
      desc: "Manage Store Events & manage Brand Ambassadors",
      accountType: 3,
      department: "support",
      leaderboard: false,
      role: "eventManager",
      permissions: [],
      dashboards: ["events"],
      views: [
        "tasks",
        "projects",
        "expenses",
        "accounts",
        "events-overview",
        "reports-overview",
      ],
    },
    {
      text: "Brand Ambassador",
      value: "brandAmbassador",
      desc: "Attend store events & educate customers in-store",
      accountType: 2,
      department: "support",
      leaderboard: true,
      role: "brandAmbassador",
      permissions: [],
      dashboards: ["brand ambassador"],
      views: ["tasks", "accounts", "events-overview"],
    },
  ],
  dashboards: [
    {
      text: "Vendor Orders",
      desc: "Manage all active vendor orders, grouped by state",
      value: "vendor orders",
    },
    {
      text: "Manufacturing",
      desc: "View incoming raw material / active work orders by selected location",
      value: "manufacturing",
    },
    {
      text: "Fulfillment",
      desc: "Shows in-progress orders & updates in real-time as changes are made",
      value: "fulfillment",
    },
    {
      text: "Sales Rep",
      desc: "View managed accounts/orders/store events/etc to a sales rep. Shows MTD orders vs. goal, current accounts receivable...",
      value: "sales rep",
    },
    {
      text: "Lead Prospector",
      desc: "Similar to Sales Rep dashboard, but focused on leads*",
      value: "lead prospector",
    },
    {
      text: "Sales Manager",
      desc: "View the details of a sales rep's dashboard. Includes option to set goals for all reps in the selected state",
      value: "sales manager",
    },
    {
      text: "Brand Ambassador",
      desc: "Filters active store events assigned to the related user",
      value: "brand ambassador",
    },
    {
      text: "Event Manager",
      desc: "View all store events in the user's associated state. Filter by events where 'Approval Required' & finalize events in 'Ready for Review'",
      value: "events",
    },
    {
      text: "Projects",
      desc: "View active projects associated with this user. Includes assigned tasks",
      value: "projects",
    },
    {
      text: "Credit Approvals",
      desc: "View & approve credit requests for a selected brand/state",
      value: "credit approval",
    },
    {
      text: "Driver",
      desc: "View assigned delivery routes based on selected day. Includes option to clock in/out",
      value: "driver",
    },
    {
      text: "Operations",
      desc: "The operations dashboard consolidates administrative tasks like setting up company info/products and a 2nd mode to manage all states at a glance",
      value: "operations",
    },
    {
      text: "Metrc",
      desc: "Create items/packages & load products/raw materials into MyTrace.",
      value: "metrc",
    },
  ],
};

const actions = {
  async _user({ commit }, payload) {
    commit("setUser", payload);
  },
  async _oAuthToken({ commit }, payload) {
    commit("setOAuthToken", payload);
  },

  /**
   * Fetch all rolePermissions documents from Firestore.
   * If the collection is empty, seeds it from defaultUserRoles first.
   */
  async fetchRolePermissions({ commit }, payload = {}) {
    const allViewValues = payload.allViewValues || [];
    const colRef = collection(firestore, "rolePermissions");
    let snap = await getDocs(colRef);

    if (snap.empty) {
      // Auto-seed from defaults; build admin role with all views if available
      for (const roleDoc of defaultUserRoles) {
        const docToWrite =
          roleDoc.value === "admin" && allViewValues.length > 0
            ? _buildAdminRoleDoc(roleDoc, allViewValues)
            : roleDoc;
        await setDoc(
          doc(firestore, "rolePermissions", roleDoc.value),
          docToWrite,
        );
      }
      snap = await getDocs(colRef);
    } else if (allViewValues.length > 0) {
      // Update existing admin role doc if its view count is stale
      const adminDocSnap = snap.docs.find((d) => d.id === "admin");
      if (adminDocSnap) {
        const existingViewCount =
          adminDocSnap.data().internal?.config?.views?.length || 0;
        if (existingViewCount !== allViewValues.length) {
          const updated = _buildAdminRoleDoc(
            adminDocSnap.data(),
            allViewValues,
          );
          await setDoc(doc(firestore, "rolePermissions", "admin"), updated);
        }
      } else {
        // Admin role doesn't exist in this instance yet — create it
        const adminBase = defaultUserRoles.find((r) => r.value === "admin");
        const newAdminDoc = _buildAdminRoleDoc(adminBase, allViewValues);
        await setDoc(doc(firestore, "rolePermissions", "admin"), newAdminDoc);
      }
    }

    // Migrate old "admin" block key → "internal" on any stale rolePermissions docs
    const staleDocs = snap.docs.filter(
      (d) => d.data().admin !== undefined && d.data().internal === undefined,
    );
    if (staleDocs.length > 0) {
      for (const staleDoc of staleDocs) {
        await updateDoc(doc(firestore, "rolePermissions", staleDoc.id), {
          internal: staleDoc.data().admin,
          admin: deleteField(),
        });
      }
      snap = await getDocs(colRef);
    }

    // Fill in missing viewPermissions for non-admin users; set role+full VP for admins
    await _migrateUserViewPermissions(snap.docs, allViewValues);

    const roles = snap.docs.map((d) => {
      const data = d.data();
      return {
        ...data,
        value: data.value || d.id,
        text: data.text || data.name || d.id,
      };
    });
    commit("setUserRoles", roles);
    return roles;
  },

  /**
   * Write (set/merge) a single role document to rolePermissions/{roleId}.
   */
  async saveRolePermission(_, roleDoc) {
    await setDoc(doc(firestore, "rolePermissions", roleDoc.value), roleDoc);
  },

  /**
   * Create a brand-new role document using buildRoleDoc and save it to Firestore.
   * @param {object} roleDef - { id, name, views, dashboards, reports, permissions, leaderboard, department }
   */
  async createRolePermission({ commit, state }, roleDef) {
    const roleDoc = buildRoleDoc(roleDef);
    await setDoc(doc(firestore, "rolePermissions", roleDoc.value), roleDoc);
    const updated = [
      ...state.userRoles.filter((r) => r.value !== roleDoc.value),
      roleDoc,
    ];
    commit("setUserRoles", updated);
    return roleDoc;
  },

  /**
   * Fetch all users matching a given role + accountType (numeric value).
   * Returns an array of { id, ...userData } objects.
   */
  async fetchUsersForRoleAndType(_, { roleValue, accountTypeValue }) {
    const q = query(
      collection(firestore, "users"),
      where("role", "==", roleValue),
      where("accountType", "==", accountTypeValue),
      where("active", "==", true),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },
};

const mutations = {
  setUser(state, payload) {
    state.user = payload;
  },
  setOAuthToken(state, payload) {
    state.oAuthToken = payload;
  },
  setUserRoles(state, payload) {
    state.userRoles = payload;
  },
};

const getters = {
  getUser(state) {
    return state.user;
  },
  getAccountTypes(state) {
    return state.accountTypes;
  },
  getUserDashboards(state) {
    return state.dashboards;
  },
  getUserRoles(state) {
    return state.userRoles;
  },
  getUserPermissions() {
    return state.mytracePermissions;
  },
  getUserPresets(state) {
    return state.userPresets;
  },
  getDepartments(state) {
    return state.departments;
  },
  getOAuthToken(state) {
    return state.oAuthToken;
  },
  /**
   * Returns the custom block for a given role + accountType numeric value.
   * Usage: this.$store.getters.resolveRoleBlock("accountExecutive", 2)
   */
  resolveRoleBlock: (state) => (roleValue, accountTypeValue) => {
    const roleDoc = state.userRoles.find((r) => r.value === roleValue);
    if (!roleDoc) return null;
    const label = _accountTypeLabel(accountTypeValue);
    if (!label) return null;
    return roleDoc[label]?.custom || null;
  },
  resolveAccountTypeBadge:
    (state) =>
    (value, isDark = false) => {
      const type = state.accountTypes.find((t) => t.value === value);
      if (!type?.badge)
        return {
          color: "grey",
          colorHex: "#9e9e9e",
          variant: "text",
          icon: "mdi-help",
        };
      const badge = isDark ? type.badge.dark : type.badge.light;
      const colorHex = resolveBadgeColorToHex(badge.color) || "#64748b";
      return { ...badge, colorHex };
    },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
